/**
 * Shared Dream Cat market-to-effect mapping for the ai-dream-cat-v1 template.
 *
 * The dropCardVariant slot value always holds an explicit variant name.
 * The frontend derives the initial value from the active market and updates it
 * automatically when the market changes (unless the operator has manually
 * selected a different variant).
 * Market codes are normalised to lowercase before lookup.
 * Falls back to the EN asset if the market is unknown or missing.
 */

/** Firebase Storage bucket used by the application for fixed overlay assets. */
const STORAGE_BUCKET = 'template-stamper-staging.firebasestorage.app';

/** Builds a Firebase Storage download URL for a given storage path. */
export const storageAssetUrl = (storagePath: string): string =>
  `https://firebasestorage.googleapis.com/v0/b/${STORAGE_BUCKET}/o/${encodeURIComponent(storagePath)}?alt=media`;

/**
 * Pre-rendered transparent WebM assets per market/language for the Dream Cat
 * effect badge overlay.  Keys are normalised uppercase market codes.
 */
export const DREAM_CAT_EFFECT_BY_MARKET = {
  KR: 'assets/videos/ai-dream-cat/drop-card-kr.webm',
  ID: 'assets/videos/ai-dream-cat/drop-card-id.webm',
  EN: 'assets/videos/ai-dream-cat/drop-card-en.webm',
  JP: 'assets/videos/ai-dream-cat/drop-card-jp.webm',
  IN: 'assets/videos/ai-dream-cat/drop-card-in.webm',
} as const;

/**
 * Explicit per-variant override map — keyed on the dropCardVariant slot value.
 * Used when the operator selects a language from the Effect Icon dropdown.
 */
export const DREAM_CAT_EFFECT_BY_VARIANT: Record<string, string> = {
  'dream-cat-kr': 'assets/videos/ai-dream-cat/drop-card-kr.webm',
  'dream-cat-id': 'assets/videos/ai-dream-cat/drop-card-id.webm',
  'dream-cat-en': 'assets/videos/ai-dream-cat/drop-card-en.webm',
  'dream-cat-jp': 'assets/videos/ai-dream-cat/drop-card-jp.webm',
  'dream-cat-in': 'assets/videos/ai-dream-cat/drop-card-in.webm',
};

/**
 * Maps canonical market codes (lowercase) to their Dream Cat drop-card variant name.
 * Used by the frontend to derive the default dropCardVariant based on the active market.
 */
export const MARKET_TO_DREAM_CAT_VARIANT: Record<string, string> = {
  kr:     'dream-cat-kr',
  id:     'dream-cat-id',
  en:     'dream-cat-en',
  jp:     'dream-cat-jp',
  in:     'dream-cat-in',
  global: 'dream-cat-en',
};

/**
 * Returns the Dream Cat drop-card variant name for the given market code.
 * Falls back to 'dream-cat-en' for unknown/missing markets.
 */
export function getMarketDefaultVariant(market?: string | null): string {
  return MARKET_TO_DREAM_CAT_VARIANT[(market || '').toLowerCase()] ?? 'dream-cat-en';
}

/**
 * Returns the Firebase Storage path for the Dream Cat drop-card effect.
 *
 * Resolution order:
 *  1. If `dropCardVariant` is set and not "auto" (legacy), use the explicit variant map.
 *  2. Otherwise resolve from `market` (normalised to uppercase), falling back to EN.
 */
export function getDreamCatEffectPath(market?: string | null, dropCardVariant?: string | null): string {
  if (dropCardVariant && dropCardVariant !== 'auto') {
    return DREAM_CAT_EFFECT_BY_VARIANT[dropCardVariant] ?? DREAM_CAT_EFFECT_BY_MARKET.EN;
  }
  const normalized = (market || '').toUpperCase();
  return (
    DREAM_CAT_EFFECT_BY_MARKET[normalized as keyof typeof DREAM_CAT_EFFECT_BY_MARKET] ??
    DREAM_CAT_EFFECT_BY_MARKET.EN
  );
}
